import { ColumnType, CustomSwitch, Input, Table } from "@/components";
import Popover from "@/components/Popover";
import { useAuth } from "@/contexts/AuthContext";
import { useDebounce } from "@/hooks/useDebounce";
import { useCompanyStore } from "@/store/companyStore";
import { useLoadingStore } from "@/store/loadingStore";
import { useModalStore } from "@/store/modalStore";
import { useUserStore } from "@/store/userStore";
import {
  BusinessTypeEnum,
  GetUsersResp,
  RoleEnum,
  RoleManagerExcute,
} from "@/types";
import { cn, ROLE_LABELS } from "@/utils";
import { filterParams } from "@/utils/filterParams";
import { useEffect, useRef, useState } from "react";
import { HiDotsVertical, HiOutlineSearch } from "react-icons/hi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DeleteUser } from "../storeDetail/components/DeleteUser";
import { DELETE_USER_IN_STORE_MODAL_NAME } from "../storeDetail/hooks";
import { KEYS_SEARCH_PARAMS } from "./CompanyDetail";
import { CreateUser } from "./CreateUser";
import { UpdateManagerGroups } from "./UpdateManagerGroups";

const FIRST_PAGE = 1;
const LIMIT = 30;
const defaultClassPopverTrigger =
  "user-menu-item px-4 py-2 cursor-pointer whitespace-nowrap hover:bg-gray-100 border-b border-gray-200";

export function UserCompanyList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  const tableRef = useRef<HTMLTableElement>(null);
  const { role } = useAuth();

  const [searchUsersInput, setSearchUsersInput] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [currentPageUser, setCurrentPageUser] = useState(1);
  const [isFetchMore, setIsFetchMore] = useState(false);
  const [record, setRecord] = useState<GetUsersResp | null>(null);

  const { fetchUsers, users, company, setUsers, updateUserStatus } =
    useCompanyStore();
  const { setLoading } = useLoadingStore();
  const { deleteUser } = useUserStore();
  const debouncedSearchUsers = useDebounce(searchUsersInput, 500);
  const params = Object.fromEntries([...searchParams]);
  const isCompanyDisabled = company?.status === "DISABLE";
  const id = company?.id;

  const currentSearchUser = params[KEYS_SEARCH_PARAMS.SEARCH_USER] || "";
  const hasMore = users?.pagination?.hasNextPage;
  const heightTableClass =
    company?.business_type === BusinessTypeEnum.NON_MEMBER
      ? "max-h-[calc(100vh-13.25rem)+1.125rem] min-h-[110px]"
      : "max-h-[calc(100vh-44.625rem+2.525rem)] min-h-[110px]";

  const { modals } = useModalStore();

  const isOpenDeleteModal = modals[DELETE_USER_IN_STORE_MODAL_NAME];
  const isUserLearningViewDisabled = (userId?: number) => {
    return (
      isCompanyDisabled ||
      !userId ||
      !RoleManagerExcute.USER_LEARNING.VIEW[role || RoleEnum.OTHER]
    );
  };
  const isUserInfoViewDisabled = (userId?: number) => {
    return (
      isCompanyDisabled ||
      !userId ||
      !RoleManagerExcute.USER_INFORMATION.VIEW[role || RoleEnum.OTHER]
    );
  };

  const usersColumns: ColumnType<GetUsersResp>[] = [
    {
      title: (
        <div className="flex items-center justify-between gap-2">
          <p>氏名</p>
        </div>
      ),
      dataIndex: "full_name",
      key: "full_name",
      width: "35%",
      render: (full_name) => <p className="truncate">{full_name}</p>,
    },
    {
      title: (
        <div className="flex items-center justify-between gap-2">
          <p> メールアドレス</p>
        </div>
      ),
      dataIndex: "email",
      key: "email",
      width: "35%",
      className: "break-all",
      render: (email) => (
        <p className="line-clamp-2" title={email}>
          {email}
        </p>
      ),
    },
    {
      title: (
        <div className="flex items-center justify-between gap-2">
          <p>役割</p>
        </div>
      ),
      dataIndex: "role",
      key: "role",
      width: "20%",
      render: (role) => (
        <span>
          {role === RoleEnum.COMPANY_ADMIN
            ? ROLE_LABELS.admin
            : ROLE_LABELS.head
          }
        </span>
      ),
    },
    {
      title: "管理",
      key: "actions",
      width: "15%",
      render: (_, record) => (
        <div className="flex flex-row items-center justify-around w-full gap-3">
          {RoleManagerExcute.USER_IN_COMPANY.ACTIVE[role || RoleEnum.OTHER] && (
            <CustomSwitch
              disabled={isCompanyDisabled || role === record?.role}
              checked={isCompanyDisabled ? false : record.status === "ENABLE"}
              onCheckedChange={() => updateUserStatus(record.id)}
              size="xs"
            />
          )}
          {RoleManagerExcute.USER_LEARNING.VIEW[role || RoleEnum.OTHER] &&
            RoleManagerExcute.USER_INFORMATION.VIEW[role || RoleEnum.OTHER] && (
              <Popover
                trigger={
                  <div className="flex items-center justify-center h-6">
                    <HiDotsVertical
                      className={cn(
                        "size-4 ",
                        isCompanyDisabled
                          ? "cursor-not-allowed text-gray-400"
                          : "hover:text-cyan",
                      )}
                    />
                  </div>
                }
                side="bottom"
                align="end"
              >
                <div
                  className={cn(
                    "user-menu-dropdown bg-white shadow-lg rounded-lg border border-gray-200 min-w-[160px]",
                    isOpenDeleteModal || isCompanyDisabled ? "hidden" : "",
                  )}
                >
                  {/* information user */}
                  <div
                    className={cn(
                      defaultClassPopverTrigger,
                      isUserInfoViewDisabled(record.id) ? "opacity-50" : "",
                    )}
                    onClick={() => {
                      if (isUserInfoViewDisabled(record.id)) return;
                      navigate(`/user-information/${record.id}`);
                    }}
                  >
                    ユーザー情報
                  </div>

                  {/* update manager groups and just for head office user */}
                  {/* CHECK PERMISSIONS FOR MOVING GROUP */}
                  {/* CHECK ROLE FOR MOVING GROUP */}
                  {RoleManagerExcute.GROUP.MOVING[role || RoleEnum.OTHER] &&
                    record?.role === RoleEnum.HEAD_OFFICE_USER && (
                      <div
                        className={cn(
                          defaultClassPopverTrigger,
                          isCompanyDisabled ? "opacity-50" : "",
                        )}
                        onClick={() => {
                          if (isCompanyDisabled) return;
                          setRecord(record);
                        }}
                      >
                        人事異動
                      </div>
                    )}

                  {/* learning details of user */}
                  <div
                    className={cn(
                      defaultClassPopverTrigger,
                      isUserLearningViewDisabled(record.id) ? "opacity-50" : "",
                    )}
                    onClick={() => {
                      if (isUserLearningViewDisabled(record.id)) return;
                      navigate(`/user-information/${record.id}/learning`);
                    }}
                  >
                    学習詳細
                  </div>

                  {/* delete user */}
                  {RoleManagerExcute.USER_IN_COMPANY.CREATE[
                    role || RoleEnum.OTHER
                  ] && (
                    <DeleteUser
                      triggerClassName="user-menu-item px-4 py-2 cursor-pointer whitespace-nowrap text-red-500 hover:bg-red-50"
                      userId={record.id}
                      onDeleteUser={onDeleteUser}
                      disabled={record?.role === RoleEnum.COMPANY_ADMIN}
                    >
                      アカウントを削除
                    </DeleteUser>
                  )}
                </div>
              </Popover>
            )}
        </div>
      ),
    },
  ];

  const handleFetchUsers = async (page?: number, s?: string) => {
    try {
      if (!id) return;
      setLoadingUsers(true);
      await fetchUsers(
        +id,
        filterParams({
          page: page || 1,
          s: s,
          limit: LIMIT,
        }),
      );
    } catch (error) {
      // TODO
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    handleFetchUsers(FIRST_PAGE, currentSearchUser);
  }, [id, currentSearchUser]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (debouncedSearchUsers?.trim()) {
      updateSearchParams({
        [KEYS_SEARCH_PARAMS.SEARCH_USER]: debouncedSearchUsers?.trim() || "",
      });
    } else {
      updateSearchParams({ [KEYS_SEARCH_PARAMS.SEARCH_USER]: "" });
    }
  }, [debouncedSearchUsers]);

  const updateSearchParams = (object: { [key: string]: string }) => {
    const inputParams: { [key: string]: string } = params;
    for (const key in object) {
      const value = object[key]?.trim();
      if (value) {
        inputParams[key] = value;
      } else {
        delete inputParams[key];
      }
    }
    setSearchParams(inputParams, { replace: true });
  };

  const handleUsersKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setSearchUsersInput(searchUsersInput);
    }
  };

  const handleScrollToBottom = () => {
    if (hasMore) {
      setIsFetchMore(true);
      const nextPage = currentPageUser + 1;
      setCurrentPageUser(nextPage);
      handleFetchUsers(nextPage, debouncedSearchUsers).then(() => {
        setIsFetchMore(false);
      });
    }
  };

  const onDeleteUser = async (id: number) => {
    if (!users) return;
    setLoading(true);
    const temp = users;
    const newData = (users?.data || []).filter((i) => i.id !== id);
    setUsers({ ...users, data: newData });
    await deleteUser(id)
      .catch(() => {
        setUsers(temp);
      })
      .finally(() => setLoading(false));
  };

  return (
    <section className="bg-white rounded-lg shadow-shadow_light_card flex-1 flex flex-col">
      <div className="flex items-center justify-between px-4 py-2 gap-4">
        <h3 className="text-lg font-semibold whitespace-nowrap">
          ユーザー一覧
        </h3>
        <div className="flex-1" />
        <Input
          placeholder="検索"
          value={searchUsersInput}
          onChange={(e) => setSearchUsersInput(e.target.value)}
          className="max-w-80 !h-9 !rounded-lg"
          icon={<HiOutlineSearch className="w-5 h-5 text-gray-400" />}
          onKeyDown={handleUsersKeyDown}
        />
        {RoleManagerExcute.USER_IN_COMPANY.CREATE[role || RoleEnum.OTHER] && (
          <CreateUser
            onDone={() => {
              tableRef?.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }}
          />
        )}
      </div>
      <div className={cn("w-full flex-1 overflow-hidden", heightTableClass)}>
        <Table<GetUsersResp>
          ref={tableRef}
          columns={usersColumns}
          dataSource={users?.data || []}
          loading={loadingUsers && !users?.data?.length}
          isFetchMore={isFetchMore}
          rowKey="id"
          bordered={true}
          className={cn(
            "border-l-0 border-r-0 rounded-b-none max-h-full visible-scrollbar",
          )}
          size="middle"
          onScrollToBottom={handleScrollToBottom}
        />
      </div>

      <UpdateManagerGroups
        open={Boolean(record?.id)}
        setOpen={() => setRecord(null)}
        handleClose={() => {
          setRecord(null);
        }}
        userName={record?.full_name || ""}
        userId={record?.id || 0}
        defaultGroup={record?.group_ids || []}
      />
    </section>
  );
}
